'use client';

import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Modal as AriaModal, Dialog, ModalOverlay } from 'react-aria-components';
import type { ModalOverlayProps } from 'react-aria-components';

import { useFusionTheme } from '../hooks';

import styles from './Modal.module.scss';
import { ModalContext } from './ModalContext';

type BaseModalProps = Pick<
  ModalOverlayProps,
  'isKeyboardDismissDisabled' | 'isOpen' | 'className' | 'onOpenChange'
>;

type DismissableProps =
  | {
      isDismissable?: false;
      shouldCloseOnInteractOutside?: never;
    }
  | {
      isDismissable: true;
      shouldCloseOnInteractOutside?: ModalOverlayProps['shouldCloseOnInteractOutside'];
    };

const modalVariants = cva(styles.modal, {
  variants: {
    isFullScreen: {
      true: styles.modalFullScreen,
      false: ''
    }
  }
});

export type Props = BaseModalProps &
  DismissableProps & {
    children: ReactNode;
    isFullScreen?: boolean;
  };

const Modal = ({
  children,
  className,
  isKeyboardDismissDisabled = true,
  isFullScreen = false,
  ...props
}: Props) => {
  const { addThemeClassName } = useFusionTheme();

  return (
    <ModalOverlay
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      {...props}
      className={addThemeClassName(styles.overlay, className)}
    >
      <AriaModal className={modalVariants({ isFullScreen })}>
        <Dialog className={styles.dialog}>
          <ModalContext.Provider value={{ isFullScreen }}>{children}</ModalContext.Provider>
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
};

export default Modal;
